import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface CreditsInfo {
  regular: number;
  topup: number;
  total: number;
  negativeLimit: number;
  hasCredits: boolean;
  isLoading: boolean;
  nextBillingDate: string | null;
}

export function useCreditsCheck(userId: string | undefined): CreditsInfo {
  const [creditsInfo, setCreditsInfo] = useState<CreditsInfo>({
    regular: 0,
    topup: 0,
    total: 0,
    negativeLimit: -300,
    hasCredits: true,
    isLoading: true,
    nextBillingDate: null,
  });

  useEffect(() => {
    if (!userId) {
      setCreditsInfo({
        regular: 0,
        topup: 0,
        total: 0,
        negativeLimit: -300,
        hasCredits: false,
        isLoading: false,
        nextBillingDate: null,
      });
      return;
    }

    async function fetchCredits() {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('credits_regular, credits_topup, credits_negative_limit, next_billing_date')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching credits:', error);
          setCreditsInfo(prev => ({ ...prev, isLoading: false }));
          return;
        }

        if (data) {
          const regular = data.credits_regular || 0;
          const topup = data.credits_topup || 0;
          const total = regular + topup;
          const negativeLimit = data.credits_negative_limit || -300;
          const hasCredits = total > negativeLimit;

          setCreditsInfo({
            regular,
            topup,
            total,
            negativeLimit,
            hasCredits,
            isLoading: false,
            nextBillingDate: data.next_billing_date,
          });
        }
      } catch (err) {
        console.error('Exception fetching credits:', err);
        setCreditsInfo(prev => ({ ...prev, isLoading: false }));
      }
    }

    fetchCredits();

    const channel = supabase
      .channel(`credits-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${userId}`,
        },
        () => {
          fetchCredits();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [userId]);

  return creditsInfo;
}

export function canAffordAction(creditsInfo: CreditsInfo, requiredCredits: number): boolean {
  if (creditsInfo.isLoading) {
    return true;
  }

  const wouldResultIn = creditsInfo.total - requiredCredits;
  return wouldResultIn >= creditsInfo.negativeLimit;
}
